import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { encodingForModel } from 'js-tiktoken';

/**
 * Constare AI Panel
 *
 * A 4-tab side panel that lives inside every lesson.
 *
 *  - Ask AI: live chat with Claude. Persisted per-user per-lesson in Turso.
 *            Enforces a server-side token budget.
 *  - Examples: curated exchanges seeded by the lesson (via loadExample),
 *              each with a "View prompt" toggle. Zero API cost.
 *  - Tokens: paste any text, see it tokenized with live count + cost preview.
 *  - Scratch: local notepad. Not saved anywhere.
 *
 * The provider takes a lessonSlug that scopes chat history and the token
 * budget on the server. The old public API (loadChatPrompt / loadTokensText /
 * openTab) still works so existing lesson pages keep functioning.
 * loadChatPrompt now routes to the Examples tab instead of the live chat.
 */

type Tab = 'ask' | 'examples' | 'tokens' | 'scratch';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

type ExampleEntry = {
  id: string;
  prompt: string;
  response: string;
};

type PanelAPI = {
  /**
   * Add or update an example in the Examples tab and switch to it. Called by
   * lesson pages when the student clicks an exercise button.
   */
  loadChatPrompt: (prompt: string, mockedResponse?: string) => void;
  /**
   * Alias for loadChatPrompt. Prefer this name in new lesson code.
   */
  loadExample: (prompt: string, mockedResponse: string, id?: string) => void;
  loadTokensText: (text: string) => void;
  openTab: (tab: Tab) => void;
};

const AIPanelContext = createContext<PanelAPI | null>(null);

export function useAIPanel(): PanelAPI {
  const ctx = useContext(AIPanelContext);
  if (!ctx) {
    throw new Error('useAIPanel must be used inside <ConstareAIPanelProvider>');
  }
  return ctx;
}

// ---------- Provider ----------

type ProviderProps = {
  /**
   * Lesson slug like "1.1", "3.4", "8.5". When present, Ask AI hits the
   * server and persists history for this (user, lesson) pair. When absent,
   * Ask AI falls back to an in-memory mock so marketing previews still work
   * for signed-out visitors.
   */
  lessonSlug?: string;
  children: React.ReactNode;
};

export function ConstareAIPanelProvider({
  lessonSlug: explicitLessonSlug,
  children,
}: ProviderProps) {
  // Fall back to inferring the lesson slug from the URL so existing pages
  // that don't pass a prop still get per-lesson chat history. Match either
  // /preview/lesson/1.2 or /lesson/1.2.
  const inferredSlug = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const m = window.location.pathname.match(/\/lesson\/(\d+\.\d+)(?:\/|$)/);
    return m ? m[1] : undefined;
  }, []);
  const lessonSlug = explicitLessonSlug ?? inferredSlug;

  const [tab, setTab] = useState<Tab>('ask');

  // ----- Ask AI state -----
  const [askInput, setAskInput] = useState('');
  const [askMessages, setAskMessages] = useState<ChatMessage[]>([]);
  const [askLoading, setAskLoading] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // ----- Examples state -----
  const [examples, setExamples] = useState<ExampleEntry[]>([]);
  const [activeExampleId, setActiveExampleId] = useState<string | null>(null);

  // ----- Tokens + Scratch -----
  const [tokensText, setTokensText] = useState('');

  // Scratch is per-week and server-persisted. weekNumber is derived from the
  // lessonSlug (e.g. "1.2" -> 1). All lessons in Week N share one scratch.
  const weekNumber = useMemo(() => {
    if (!lessonSlug) return null;
    const m = lessonSlug.match(/^(\d+)\./);
    return m ? Number.parseInt(m[1], 10) : null;
  }, [lessonSlug]);

  const [scratch, setScratchLocal] = useState('');
  const [scratchLoaded, setScratchLoaded] = useState(false);
  const scratchLoadedForWeek = useRef<number | null>(null);
  const scratchSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the week's scratch on mount / when the week changes. Signed-out
  // marketing previews skip this and just use local state.
  useEffect(() => {
    if (weekNumber === null) {
      setScratchLoaded(true);
      return;
    }
    if (scratchLoadedForWeek.current === weekNumber) return;
    let cancelled = false;
    setScratchLoaded(false);
    (async () => {
      try {
        const resp = await fetch(`/api/scratch/${weekNumber}`, {
          credentials: 'include',
        });
        if (!resp.ok) {
          if (!cancelled) {
            setScratchLocal('');
            setScratchLoaded(true);
            scratchLoadedForWeek.current = weekNumber;
          }
          return;
        }
        const data = (await resp.json()) as { content?: string };
        if (!cancelled) {
          setScratchLocal(data.content ?? '');
          setScratchLoaded(true);
          scratchLoadedForWeek.current = weekNumber;
        }
      } catch {
        if (!cancelled) {
          setScratchLoaded(true);
          scratchLoadedForWeek.current = weekNumber;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekNumber]);

  // setScratch schedules a debounced save to the server (2s after last edit).
  const setScratch = useCallback(
    (next: string) => {
      setScratchLocal(next);
      if (weekNumber === null) return;
      if (!scratchLoaded) return;
      if (scratchSaveTimer.current) clearTimeout(scratchSaveTimer.current);
      scratchSaveTimer.current = setTimeout(() => {
        void fetch(`/api/scratch/${weekNumber}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: next }),
        }).catch(() => {
          // Best-effort autosave. The next edit will retry.
        });
      }, 2000);
    },
    [weekNumber, scratchLoaded]
  );

  // Flush any pending save on unmount.
  useEffect(() => {
    return () => {
      if (scratchSaveTimer.current) clearTimeout(scratchSaveTimer.current);
    };
  }, []);

  // Load prior chat history when lessonSlug is known. Signed-out marketing
  // previews skip this call.
  useEffect(() => {
    if (!lessonSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/ai/history/${encodeURIComponent(lessonSlug)}`, {
          credentials: 'include',
        });
        if (!resp.ok) {
          // 401/403 for signed-out preview visitors: silently ignore.
          if (!cancelled) setHistoryLoaded(true);
          return;
        }
        const data = (await resp.json()) as {
          messages: Array<{ id: string; role: 'user' | 'assistant'; content: string }>;
          usage: { used: number; limit: number };
        };
        if (cancelled) return;
        setAskMessages(data.messages);
        setUsage(data.usage);
        setHistoryLoaded(true);
      } catch {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lessonSlug]);

  const sendAsk = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || askLoading) return;

      // Optimistic user message so the UI feels instant.
      const optimisticId = `local-${Date.now()}`;
      setAskMessages((m) => [
        ...m,
        { id: optimisticId, role: 'user', content: trimmed },
      ]);
      setAskInput('');
      setAskLoading(true);
      setAskError(null);

      if (!lessonSlug) {
        // Signed-out preview: no real backend. Show a friendly message.
        await new Promise((r) => setTimeout(r, 300));
        setAskMessages((m) => [
          ...m,
          {
            id: `local-${Date.now()}-r`,
            role: 'assistant',
            content:
              "This is the marketing preview of the AI panel. Sign in and start a lesson to talk to the real AI.",
          },
        ]);
        setAskLoading(false);
        return;
      }

      try {
        const resp = await fetch('/api/ai/chat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ lessonSlug, message: trimmed }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const message =
            (data && typeof data === 'object' && 'message' in data
              ? (data as { message?: string }).message
              : null) ??
            (resp.status === 429
              ? "You've reached the AI budget for this lesson."
              : resp.status === 503
              ? 'AI chat is not configured yet.'
              : 'Something went wrong. Try again in a moment.');
          setAskError(message);
          return;
        }
        const reply = (data as { reply?: ChatMessage }).reply;
        if (reply) {
          setAskMessages((m) => [...m, reply]);
        }
        const nextUsage = (data as { usage?: { used: number; limit: number } }).usage;
        if (nextUsage) setUsage(nextUsage);
      } catch (err) {
        console.error('[AskAI] send failed', err);
        setAskError('Network hiccup. Try again.');
      } finally {
        setAskLoading(false);
      }
    },
    [lessonSlug, askLoading]
  );

  const resetHistory = useCallback(async () => {
    if (!lessonSlug) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm('Clear this chat for this lesson? Prior messages will be deleted.')
    )
      return;
    try {
      await fetch(`/api/ai/history/${encodeURIComponent(lessonSlug)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch {
      // ignore
    }
    setAskMessages([]);
  }, [lessonSlug]);

  const api = useMemo<PanelAPI>(
    () => ({
      loadChatPrompt: (prompt, mockedResponse) => {
        setTab('examples');
        const id = `ex-${hash(prompt)}`;
        setExamples((prev) => {
          const exists = prev.find((e) => e.id === id);
          if (exists) return prev;
          return [
            ...prev,
            { id, prompt, response: mockedResponse ?? GENERIC_MOCK },
          ];
        });
        setActiveExampleId(id);
      },
      loadExample: (prompt, mockedResponse, id) => {
        setTab('examples');
        const finalId = id ?? `ex-${hash(prompt)}`;
        setExamples((prev) => {
          const exists = prev.find((e) => e.id === finalId);
          if (exists) return prev;
          return [...prev, { id: finalId, prompt, response: mockedResponse }];
        });
        setActiveExampleId(finalId);
      },
      loadTokensText: (text) => {
        setTab('tokens');
        setTokensText(text);
      },
      openTab: (t) => setTab(t),
    }),
    []
  );

  const panelState: PanelState = {
    tab,
    setTab,
    lessonSlug,
    weekNumber,
    askInput,
    setAskInput,
    askMessages,
    askLoading,
    askError,
    usage,
    historyLoaded,
    sendAsk,
    resetHistory,
    examples,
    activeExampleId,
    setActiveExampleId,
    tokensText,
    setTokensText,
    scratch,
    setScratch,
    scratchLoaded,
  };

  return (
    <AIPanelContext.Provider value={api}>
      <PanelStateContext.Provider value={panelState}>
        {children}
      </PanelStateContext.Provider>
    </AIPanelContext.Provider>
  );
}

// Internal state context, kept separate so lesson code doesn't accidentally
// couple to it.
type PanelState = {
  tab: Tab;
  setTab: (t: Tab) => void;
  lessonSlug?: string;
  weekNumber: number | null;

  askInput: string;
  setAskInput: (s: string) => void;
  askMessages: ChatMessage[];
  askLoading: boolean;
  askError: string | null;
  usage: { used: number; limit: number } | null;
  historyLoaded: boolean;
  sendAsk: (text: string) => void;
  resetHistory: () => void;

  examples: ExampleEntry[];
  activeExampleId: string | null;
  setActiveExampleId: (id: string) => void;

  tokensText: string;
  setTokensText: (s: string) => void;
  scratch: string;
  setScratch: (s: string) => void;
  scratchLoaded: boolean;
};

const PanelStateContext = createContext<PanelState | null>(null);

// ---------- The visible panel ----------

export function ConstareAIPanel() {
  const state = useContext(PanelStateContext);
  if (!state) {
    return (
      <div className="rounded-lg border border-line bg-surface p-4 text-xs text-muted">
        AI panel needs ConstareAIPanelProvider higher in the tree.
      </div>
    );
  }
  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col overflow-hidden rounded-lg border border-line bg-surface">
      <Header />
      <Tabs />
      <div className="flex-1 overflow-hidden">
        {state.tab === 'ask' && <AskTab />}
        {state.tab === 'examples' && <ExamplesTab />}
        {state.tab === 'tokens' && <TokensTab />}
        {state.tab === 'scratch' && <ScratchTab />}
      </div>
      <Footer />
    </div>
  );
}

function Header() {
  const s = useContext(PanelStateContext)!;
  return (
    <div className="flex items-center justify-between border-b border-line px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-amber" />
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted">
          Constare AI
        </span>
      </div>
      {!s.lessonSlug && (
        <span className="rounded-full border border-amber/30 bg-amber/10 px-2 py-0.5 text-[10px] text-amber">
          Preview
        </span>
      )}
      {s.lessonSlug && (
        <span className="font-mono text-[10px] text-muted">Lesson {s.lessonSlug}</span>
      )}
    </div>
  );
}

function Tabs() {
  const s = useContext(PanelStateContext)!;
  const tabs: { id: Tab; label: string }[] = [
    { id: 'ask', label: 'Ask AI' },
    { id: 'examples', label: 'Examples' },
    { id: 'tokens', label: 'Tokens' },
    { id: 'scratch', label: 'Scratch' },
  ];
  return (
    <div className="border-b border-line">
      <div className="flex gap-1 px-2 pt-2">
        {tabs.map((t) => {
          const active = s.tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => s.setTab(t.id)}
              className={`rounded-t-md px-3 py-1.5 text-xs transition-colors ${
                active
                  ? 'bg-elevated text-text'
                  : 'text-muted hover:bg-elevated/60 hover:text-text'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Footer() {
  const s = useContext(PanelStateContext)!;
  if (s.tab === 'ask' && s.usage) {
    const pct = Math.min(100, Math.round((s.usage.used / s.usage.limit) * 100));
    return (
      <div className="border-t border-line px-4 py-2.5">
        <div className="flex items-center justify-between text-[10px] text-muted">
          <span className="font-mono uppercase tracking-widest">Lesson AI budget</span>
          <span className="font-mono">
            {s.usage.used.toLocaleString()} / {s.usage.limit.toLocaleString()} ({pct}%)
          </span>
        </div>
        <div className="mt-1 h-1 overflow-hidden rounded bg-line">
          <div
            className={`h-full ${pct > 90 ? 'bg-red-500' : 'bg-amber'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }
  return null;
}

// ---------- Ask AI tab ----------

function AskTab() {
  const s = useContext(PanelStateContext)!;
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [s.askMessages, s.askLoading]);

  useEffect(() => {
    if (s.askInput && inputRef.current) {
      inputRef.current.focus();
      const len = s.askInput.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [s.askInput]);

  const showBudgetWarn =
    s.usage && s.usage.used / s.usage.limit > 0.9 && s.usage.used < s.usage.limit;
  const budgetHit = s.usage && s.usage.used >= s.usage.limit;

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {!s.lessonSlug && (
          <div className="rounded-md border border-dashed border-line/70 bg-ink/40 p-3 text-xs text-muted">
            You're viewing the marketing preview. Sign in and start a lesson to talk to
            the real AI. Or check out the Examples tab for curated exchanges from this
            lesson.
          </div>
        )}
        {s.lessonSlug && s.askMessages.length === 0 && !s.askLoading && (
          <div className="rounded-md border border-dashed border-line/70 bg-ink/40 p-3 text-xs text-muted">
            Talk to the AI about anything in this lesson. Ask for an angle, a critique,
            or a check on your thinking. The AI is a teammate: use it to think harder,
            not to skip the thinking.
          </div>
        )}
        {s.askMessages.map((m) => (
          <Bubble key={m.id} role={m.role} text={m.content} />
        ))}
        {s.askLoading && <Bubble role="assistant" text="…" />}
        {s.askError && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {s.askError}
          </div>
        )}
        {showBudgetWarn && (
          <div className="rounded-md border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber">
            Heads up: you're near your AI budget for this lesson.
          </div>
        )}
      </div>
      <div className="border-t border-line p-3">
        {s.lessonSlug && s.askMessages.length > 0 && (
          <div className="mb-2 flex justify-end">
            <button
              onClick={() => s.resetHistory()}
              className="text-[10px] text-muted hover:text-text"
            >
              Clear chat
            </button>
          </div>
        )}
        <div className="rounded-md border border-line bg-ink/60 focus-within:border-amber">
          <textarea
            ref={inputRef}
            value={s.askInput}
            onChange={(e) => s.setAskInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                s.sendAsk(s.askInput);
              }
            }}
            placeholder={
              budgetHit
                ? 'Budget reached for this lesson.'
                : 'Ask the AI anything about this lesson…'
            }
            disabled={!!budgetHit}
            rows={Math.min(6, Math.max(2, s.askInput.split('\n').length))}
            className="w-full resize-none bg-transparent px-3 py-2 text-sm text-text placeholder:text-muted/70 focus:outline-none disabled:opacity-60"
          />
          <div className="flex items-center justify-between border-t border-line/60 px-2 py-1.5">
            <span className="font-mono text-[10px] text-muted">
              {roughTokenCount(s.askInput).toLocaleString()} tokens
            </span>
            <button
              onClick={() => s.sendAsk(s.askInput)}
              disabled={!s.askInput.trim() || s.askLoading || !!budgetHit}
              className="rounded-md bg-amber px-3 py-1 text-xs font-semibold text-ink disabled:bg-elevated disabled:text-muted"
            >
              {s.askLoading ? 'Thinking…' : 'Send \u2192'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, text }: { role: 'user' | 'assistant'; text: string }) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border border-amber/30 bg-amber/[0.04] px-3 py-2 text-sm text-text">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] rounded-lg border border-line bg-elevated px-3 py-2 text-sm text-text/90">
        <MarkdownBody text={text} />
      </div>
    </div>
  );
}

/**
 * Render assistant markdown. Claude responses lean on **bold**, bullet lists,
 * numbered lists, inline code, and the occasional code block. We keep the
 * styling tight so the bubble still feels like a chat message, not a doc.
 */
function MarkdownBody({ text }: { text: string }) {
  return (
    <div className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p className="my-2 first:mt-0 last:mb-0 whitespace-pre-wrap">{children}</p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-text">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => (
            <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2 ml-5 list-decimal space-y-1">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-snug">{children}</li>,
          h1: ({ children }) => (
            <h1 className="mt-3 mb-1 text-sm font-semibold text-text">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-3 mb-1 text-sm font-semibold text-text">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-2 mb-1 text-sm font-semibold text-text">{children}</h3>
          ),
          code: ({ inline, children, ...rest }: any) =>
            inline ? (
              <code
                className="rounded bg-ink/70 px-1 py-0.5 font-mono text-[12px] text-amber"
                {...rest}
              >
                {children}
              </code>
            ) : (
              <code
                className="block whitespace-pre-wrap break-words rounded-md bg-ink/70 p-2 font-mono text-[12px] text-text/90"
                {...rest}
              >
                {children}
              </code>
            ),
          pre: ({ children }) => (
            <pre className="my-2 overflow-x-auto rounded-md bg-ink/70 p-2">{children}</pre>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber underline underline-offset-2 hover:text-amber-hover"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-amber/40 pl-3 text-muted">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3 border-line" />,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

// ---------- Examples tab ----------

function ExamplesTab() {
  const s = useContext(PanelStateContext)!;
  const active = s.examples.find((e) => e.id === s.activeExampleId) ?? s.examples[0];
  const [showPrompt, setShowPrompt] = useState(true);

  if (s.examples.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md rounded-md border border-dashed border-line/70 bg-ink/40 p-4 text-center text-xs text-muted">
          This lesson has no curated examples yet. Try the exercise buttons in the
          lesson to load one, or switch to Ask AI to talk to Claude directly.
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Example picker */}
      {s.examples.length > 1 && (
        <div className="border-b border-line px-3 py-2">
          <div className="flex flex-wrap gap-1.5">
            {s.examples.map((ex, i) => (
              <button
                key={ex.id}
                onClick={() => s.setActiveExampleId(ex.id)}
                className={`rounded-md px-2 py-1 text-[11px] transition ${
                  ex.id === active?.id
                    ? 'bg-amber text-ink'
                    : 'bg-elevated text-muted hover:text-text'
                }`}
              >
                Example {i + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {active && (
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <div>
            <button
              onClick={() => setShowPrompt((v) => !v)}
              className="font-mono text-[10px] uppercase tracking-widest text-muted hover:text-text"
            >
              {showPrompt ? 'Hide prompt \u2191' : 'View prompt \u2193'}
            </button>
            {showPrompt && (
              <div className="mt-2">
                <Bubble role="user" text={active.prompt} />
              </div>
            )}
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
              AI response
            </p>
            <div className="mt-2">
              <Bubble role="assistant" text={active.response} />
            </div>
          </div>
          <div className="rounded-md border border-line/60 bg-ink/40 p-2.5 text-[11px] text-muted">
            Curated example. Frozen output, so we don't spend tokens showing you the same
            thing every visit. Try the same idea in Ask AI to see how the live model
            responds to your version.
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Tokens tab ----------

let _enc: ReturnType<typeof encodingForModel> | null = null;
function getEncoder() {
  if (!_enc) _enc = encodingForModel('gpt-4o');
  return _enc;
}

const PRICING: Record<string, { input: number; output: number; label: string }> = {
  'gpt-4o': { input: 2.5, output: 10, label: 'GPT-4o' },
  'gpt-4o-mini': { input: 0.15, output: 0.6, label: 'GPT-4o-mini' },
  'claude-sonnet-4-5': { input: 3, output: 15, label: 'Claude Sonnet 4.5' },
  'claude-haiku': { input: 0.8, output: 4, label: 'Claude Haiku' },
};

function TokensTab() {
  const s = useContext(PanelStateContext)!;
  const [model, setModel] = useState<keyof typeof PRICING>('gpt-4o-mini');

  const { tokens, ids } = useMemo(() => {
    if (!s.tokensText) return { tokens: [] as string[], ids: [] as number[] };
    try {
      const enc = getEncoder();
      const tokenIds = enc.encode(s.tokensText);
      const decoded = tokenIds.map((id) => enc.decode([id]));
      return { tokens: decoded, ids: tokenIds };
    } catch {
      return { tokens: [], ids: [] };
    }
  }, [s.tokensText]);

  const tokenCount = tokens.length;
  const wordCount = useMemo(
    () => (s.tokensText.trim() ? s.tokensText.trim().split(/\s+/).length : 0),
    [s.tokensText]
  );
  const charCount = s.tokensText.length;

  const pricing = PRICING[model];
  const inputCost = (tokenCount / 1_000_000) * pricing.input;
  const outputCost = ((tokenCount * 2) / 1_000_000) * pricing.output;
  const exchangeCost = inputCost + outputCost;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="space-y-3 p-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Paste or type any text
          </label>
          <textarea
            value={s.tokensText}
            onChange={(e) => s.setTokensText(e.target.value)}
            placeholder="Try: your own name, a tweet, a code snippet, antidisestablishmentarianism, an emoji…"
            rows={4}
            className="mt-1 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Stat label="Tokens" value={tokenCount.toLocaleString()} accent />
          <Stat label="Words" value={wordCount.toLocaleString()} />
          <Stat label="Chars" value={charCount.toLocaleString()} />
        </div>

        {tokens.length > 0 && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
              Tokens (hover for ID)
            </p>
            <div className="mt-2 rounded-md border border-line bg-ink/60 p-2 leading-loose break-words">
              {tokens.map((t, i) => (
                <TokenChip key={i} index={i} text={t} id={ids[i]} />
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
              Cost preview
            </p>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value as keyof typeof PRICING)}
              className="rounded border border-line bg-ink/60 px-2 py-0.5 text-[11px] text-text"
            >
              {Object.entries(PRICING).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-2 space-y-1.5 rounded-md border border-line bg-elevated p-3 text-xs">
            <CostRow label="Send this prompt once" value={formatCost(inputCost)} />
            <CostRow
              label="Full exchange (prompt + reply)"
              value={formatCost(exchangeCost)}
              emphasized
            />
            <CostRow label="× 1,000 exchanges" value={formatCost(exchangeCost * 1000)} />
            <CostRow
              label="× 100,000 exchanges"
              value={formatCost(exchangeCost * 100_000)}
            />
          </div>
          <p className="mt-2 text-[10px] text-muted">
            Assumes a reply about 2x the length of your prompt. Real costs vary with
            response length.
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-md border p-2.5 text-center ${
        accent ? 'border-amber/30 bg-amber/[0.04]' : 'border-line bg-elevated'
      }`}
    >
      <div
        className={`font-mono text-lg tabular-nums ${
          accent ? 'text-amber' : 'text-text'
        }`}
      >
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted">
        {label}
      </div>
    </div>
  );
}

function CostRow({
  label,
  value,
  emphasized,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        emphasized ? 'text-text' : 'text-muted'
      }`}
    >
      <span>{label}</span>
      <span className={`font-mono tabular-nums ${emphasized ? 'text-amber' : ''}`}>
        {value}
      </span>
    </div>
  );
}

function TokenChip({ index, text, id }: { index: number; text: string; id: number }) {
  const tint = index % 2 === 0 ? 'bg-amber/[0.10]' : 'bg-amber/[0.18]';
  const display = text === '' ? '\u2205' : text.replace(/\n/g, '\u21b5');
  return (
    <span
      title={`Token ${index + 1} \u00b7 ID ${id}`}
      className={`mr-0.5 inline-block rounded px-1 font-mono text-[12px] text-text ${tint}`}
    >
      {display.split('').map((c, i) =>
        c === ' ' ? (
          <span key={i} className="inline-block w-1.5">
            &nbsp;
          </span>
        ) : (
          <span key={i}>{c}</span>
        )
      )}
    </span>
  );
}

// ---------- Scratch tab ----------

function ScratchTab() {
  const s = useContext(PanelStateContext)!;
  const week = s.weekNumber;

  const download = () => {
    if (week === null) return;
    const content = s.scratch ?? '';
    const filename = `Constare-Week-${week}-Scratch.txt`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const hasContent = (s.scratch ?? '').length > 0;

  return (
    <div className="flex h-full flex-col p-4">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          {week !== null ? `Week ${week} · Scratch` : 'Scratch pad'}
        </p>
        {week !== null && (
          <button
            type="button"
            onClick={download}
            disabled={!hasContent}
            className="rounded border border-line px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted transition hover:border-amber hover:text-amber disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-line disabled:hover:text-muted"
            title={hasContent ? `Download as Constare-Week-${week}-Scratch.txt` : 'Nothing to download yet'}
          >
            Download .txt
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted">
        {week !== null
          ? `Shared across every lesson in Week ${week}. Saved to your account.`
          : 'Your working space.'}
      </p>
      <textarea
        value={s.scratch}
        onChange={(e) => s.setScratch(e.target.value)}
        placeholder={
          week !== null && !s.scratchLoaded
            ? 'Loading your Week scratch…'
            : 'Notes, draft answers, things you want to remember…'
        }
        readOnly={week !== null && !s.scratchLoaded}
        className="mt-3 flex-1 resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
      />
    </div>
  );
}

// ---------- Helpers ----------

function roughTokenCount(s: string): number {
  if (!s) return 0;
  try {
    return getEncoder().encode(s).length;
  } catch {
    return Math.ceil(s.length / 4);
  }
}

function formatCost(usd: number): string {
  if (usd < 0.0001) return '<$0.0001';
  if (usd < 1) return `$${usd.toFixed(4)}`;
  if (usd < 100) return `$${usd.toFixed(2)}`;
  return `$${Math.round(usd).toLocaleString()}`;
}

// Small deterministic hash so repeated loadChatPrompt calls with the same
// prompt produce the same example id (idempotent).
function hash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return Math.abs(h).toString(36);
}

const GENERIC_MOCK = `This is a curated example. The response is frozen so you can revisit it without spending tokens each time.

To ask this same question live, switch to the Ask AI tab and type your own version. The real model will respond in your own voice, to your own words.`;
